import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { clientPosToWorldPos } from '@/editor/utils';
import { getRoomId } from '@/editor/data/room/utils';
import { CommonController2d } from '../CommonController';
import { EEntityType, EOperateMode, EToolType } from '@/editor/types';
import { IRulerInfo, RulerModel } from '@/editor/entities/2d/rulerModel';
import VirtualWallModel, { EVirtualWallStatus } from '@/editor/entities/2d/virtualWallModel';
import { convertSepWallTypeToVirtualWallStyle } from '@/editor/utils/wall';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { WallPointAbsorbManager } from '@/editor/manager/absorb/wall/wallPointAbsorbManager';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { ESepcialKey } from '@/editor/stores/keyboardStore';
import { showToast } from '@/editor/utils/feedback';
import { MAX_WALL_LENGTH, MIN_WALL_LENGTH } from '@/editor/constant/wall';
import { FloatingPoint } from '@/editor/math/number';
import i18n from '@/i18n';

class DrawSeparateWall extends CommonController2d {
  private _relateRoomId?: string;
  private _currentPoint: IPoint2d | null = null;
  private _movePoint: IPoint2d | null = null;

  private _virtualWallModel: VirtualWallModel;
  private _virtualRulerModel: RulerModel;

  private _adsorbManager = new WallPointAbsorbManager(globalStores.separateWallStore, EEntityType.SeparateWall);
  private _eventDispose: () => void;

  constructor(_gs: TGlobalStores) {
    super(_gs);

    this._virtualWallModel = new VirtualWallModel();
    this._virtualRulerModel = new RulerModel({
      onChange: this.handleRulerChange.bind(this),
      onCommit: this.handleRulerCommit.bind(this)
    });

    this._adsorbManager.updateWallData();

    this._eventDispose = editorEvent.on(EditorEventName.KEYBOARD_KEY_PRESS, (key: string) => {
      if (key === ESepcialKey.Escape) {
        if (!this._currentPoint) {
          this._gs.operateInfo = { mode: EOperateMode.EMPTY, toolType: EToolType.empty };
        } else {
          this.clear();
        }
      }
    });
  }

  private getSeparateWallType() {
    return globalStores.operateInfo.toolType === EToolType.virtualSeparateWall
      ? ESeparateWallType.VirtualWall
      : globalStores.paramStore.separateWallType;
  }

  private getSeparateWallThickness() {
    return this.getSeparateWallType() === ESeparateWallType.VirtualWall
      ? 0
      : globalStores.paramStore.separateWallThickness;
  }

  private isWallLengthLegal(start: IPoint2d, end: IPoint2d) {
    return FloatingPoint.between(Point2dUtils.distanceTo(start, end), MIN_WALL_LENGTH, MAX_WALL_LENGTH);
  }

  private setPoint(point: IPoint2d) {
    this._currentPoint = point;
    this._adsorbManager.setOrthogonalData(point);
    this._adsorbManager.lockSeparateWallRoom(this._relateRoomId);
  }

  private getRulerEndPoint(value: number, rulerInfo: IRulerInfo) {
    const rulerDirection = Point2dUtils.getUnitDirection(rulerInfo.start, rulerInfo.end);
    return Point2dUtils.addDistance(rulerInfo.start, value, rulerDirection);
  }

  private updateVirtualWall(endPoint: IPoint2d, isLegal: boolean) {
    if (!this._currentPoint || !this._relateRoomId) return;

    const separateWallType = this.getSeparateWallType();
    const thickness = this.getSeparateWallThickness();

    this._virtualWallModel.update([
      {
        start: this._currentPoint,
        end: endPoint,
        thickness,
        style: convertSepWallTypeToVirtualWallStyle(separateWallType),
        status: isLegal ? EVirtualWallStatus.Normal : EVirtualWallStatus.Unnormal
      }
    ]);
    this._virtualRulerModel.resetFocusId();
    this._virtualRulerModel.updateModel(
      [{ id: '0', start: this._currentPoint, end: endPoint, editable: true, focusTrap: true, max: MAX_WALL_LENGTH }],
      '0'
    );
  }

  private handleRulerChange = (id: string, value: number, rulerInfo: IRulerInfo) => {
    if (!this._currentPoint || !this._relateRoomId) return;
    if (Point2dUtils.distanceTo(rulerInfo.start, rulerInfo.end) < MIN_WALL_LENGTH) return;

    const newEndPoint = this.getRulerEndPoint(value, rulerInfo);
    const lengthLegal = this.isWallLengthLegal(this._currentPoint, newEndPoint);
    const { isLegal } = lengthLegal
      ? globalStores.separateWallOperation.isLegal(this._currentPoint, newEndPoint, this._relateRoomId)
      : { isLegal: false };

    if (!isLegal) this._adsorbManager.clearModel();
    this._movePoint = newEndPoint;
    this.updateVirtualWall(newEndPoint, isLegal);
  };

  private handleRulerCommit = (id: string, value: number, rulerInfo: IRulerInfo) => {
    if (!this._currentPoint || !this._relateRoomId) return;
    if (Point2dUtils.distanceTo(rulerInfo.start, rulerInfo.end) < MIN_WALL_LENGTH) return;

    const newEndPoint = this.getRulerEndPoint(value, rulerInfo);
    if (!this.isWallLengthLegal(this._currentPoint, newEndPoint)) return;

    const { isLegal } = globalStores.separateWallOperation.isLegal(this._currentPoint, newEndPoint, this._relateRoomId);
    if (!isLegal) {
      showToast(i18n.t('draw.warning.sep_wall_intersecting'));
      return;
    }

    const added = this.drawSeparateWall(newEndPoint);
    if (added) this.setPoint(newEndPoint);
  };

  private drawSeparateWall(point: IPoint2d) {
    if (!this._relateRoomId || !this._currentPoint) return;
    if (!this.isWallLengthLegal(this._currentPoint, point)) return false;

    // 开始添加隔墙
    const added = globalStores.separateWallOperation.addSeparateWall(this._currentPoint, point, this._relateRoomId);

    if (added) {
      this._virtualWallModel.clear();
      this._virtualRulerModel.clear();
    }

    return added;
  }

  // 开始/结束绘制墙体时会有很多状态设定
  protected override mouseUp = (e: MouseEvent, isDragged: boolean) => {
    if (isDragged) return;

    if (e.button === 2) {
      // 右键
      if (!this._currentPoint) {
        this.dispose();
        this.exit();
      } else {
        this.clear();
      }

      return;
    }

    if (e.button !== 0) return;

    if (!this._movePoint) return;
    const adsorbPoint = this._movePoint;

    // 添加终点
    if (this._relateRoomId && this._currentPoint) {
      if (!this.isWallLengthLegal(this._currentPoint, adsorbPoint)) return;

      const { isLegal } = globalStores.separateWallOperation.isLegal(
        this._currentPoint,
        adsorbPoint,
        this._relateRoomId
      );

      if (!isLegal) return;

      const added = this.drawSeparateWall(adsorbPoint);

      if (added) this.setPoint(adsorbPoint);

      return;
    }

    // 添加起点
    if (this._currentPoint) return; // 已经有起点，退出

    const roomId = getRoomId(globalStores.roomStore.getRoomDatas(), adsorbPoint);
    if (!roomId) return;

    this._relateRoomId = roomId;
    this.setPoint(adsorbPoint);
  };

  protected override mouseMove = (e: MouseEvent) => {
    const startPoint = this._currentPoint;
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const point = clientPosToWorldPos(e, camera, renderer);
    const adsorbPoint = this._adsorbManager.calculateAbsorbPointThenRender(point) || point;
    this._movePoint = adsorbPoint;

    if (!startPoint || !this._relateRoomId) return;

    const lengthLegal = this.isWallLengthLegal(startPoint, adsorbPoint);
    const { isLegal } = lengthLegal
      ? globalStores.separateWallOperation.isLegal(startPoint, adsorbPoint, this._relateRoomId)
      : { isLegal: false };
    if (!isLegal) {
      this._adsorbManager.clearModel();
    }
    this.updateVirtualWall(adsorbPoint, isLegal);
  };

  clear() {
    this._currentPoint = null;
    this._movePoint = null;
    this._relateRoomId = undefined;
    this._virtualWallModel.clear();
    this._virtualRulerModel.clear();
    this._adsorbManager.clearOrthogonalData();
    this._adsorbManager.clearModel();
    this._adsorbManager.clearSeparateWallRoom();
  }

  override dispose() {
    this.clear();
    this._eventDispose();

    this._virtualWallModel.dispose();
    this._virtualRulerModel.dispose();
    this._adsorbManager.dispose();
    super.dispose();
  }
}

export default DrawSeparateWall;
